<template>
  <b-card
    class="overflow-hidden p-0"
    no-body
  >
    <!-- Vista móvil: tarjetas legibles -->
    <div
      v-if="isSmallScreen"
      class="expense-cards"
    >
      <div
        v-for="item in sortedExpenses"
        :key="item.id"
        class="expense-card"
      >
        <div class="expense-card__top">
          <router-link
            v-if="groupLinkTarget(item)"
            :to="groupLinkTarget(item)"
            class="expense-card__title"
          >
            {{ cardTitle(item) }}
          </router-link>
          <span
            v-else
            class="expense-card__title expense-card__title--plain"
          >
            {{ cardTitle(item) }}
          </span>
          <span class="expense-card__amount">${{ item.total | money }}</span>
        </div>

        <div class="expense-card__sub">
          <span v-if="item.expense_type_catalog && item.expense_type_catalog.name">
            {{ item.expense_type_catalog.name }}
          </span>
          <span v-if="item.supplier"> · {{ item.supplier }}</span>
          <span v-if="showProjectColumn"> · {{ projectName(item) }}</span>
        </div>

        <div class="expense-card__meta">
          <span>{{ item.expense_date | date }}</span>
          <span v-if="isMaterial && Number(item.quantity)">
            · {{ item.quantity }} {{ $tc(unitsFilter(item.unit_type), item.quantity) }} × ${{ item.unit_price | money }}
          </span>
          <router-link
            v-if="item.quote_id"
            :to="{ name: 'purchaseOrder', params: { quoteId: item.quote_id } }"
            class="expense-card__quote"
          >
            OC #{{ item.quote_folio }}
          </router-link>
        </div>

        <div class="expense-card__actions">
          <b-dropdown
            :id="`st-${item.id}`"
            class="p-0"
            size="sm"
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <b-badge
                :variant="statusColor(item.expense_status)"
                class="px-1"
              >
                {{ $t(item.expense_status) }}
                <feather-icon icon="ChevronDownIcon" size="14" />
              </b-badge>
            </template>
            <b-dropdown-item
              v-for="(status, key) in expenseStatusOptions"
              :key="key"
              @click="changeStatus(item.id, status.value)"
            >
              {{ $t(status.value) }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            :id="`tl-${item.id}`"
            class="p-0"
            size="sm"
            right
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="18" />
            </template>
            <b-dropdown-item @click="viewInfoExpenseModal(item)">
              Ver detalle
            </b-dropdown-item>
            <b-dropdown-item @click="openEditModal(item)">
              {{ $t('edit') }}
            </b-dropdown-item>
            <b-dropdown-item @click="removeExpense(item.id)">
              <span class="text-danger">{{ $t('delete') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>

    <!-- Escritorio: tabla -->
    <div
      v-else
      class="table-responsive-mobile"
    >
      <b-table
        show-empty
        :busy="loading"
        :items="sortedExpenses"
        :fields="tableFields"
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @sort-changed="onSortChanged"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="5"
            :columns="tableFields.length"
            :table-props="{ small: true }"
          />
        </template>

        <template #empty>
          <table-empty-state
            icon="TrendingDownIcon"
            title="Todavía no hay egresos"
            hint="Registra un egreso para llevar el control de gastos de la obra."
          />
        </template>

        <!-- Personalización de columnas -->
        <template #cell(folio)="data">
          <div class="text-primary font-weight-bolder">
            #{{ data.value }}
          </div>
        </template>

        <template #cell(name)="data">
          <router-link
            v-if="groupLinkTarget(data.item)"
            :to="groupLinkTarget(data.item)"
            class="text-primary font-weight-bolder"
          >
            {{ !isMaterial ? (data.item.description || data.value) : data.value }}
          </router-link>
          <span
            v-else
            class="font-weight-bolder"
          >
            {{ !isMaterial ? (data.item.description || data.value) : data.value }}
          </span>
        </template>

        <template #cell(description)="data">
          <feather-icon
            :id="`tooltip-target-${data.index}`"
            icon="InfoIcon"
            size="15"
            stroke="black"
            @click="viewInfoExpenseModal(data.item)"
          />
          <!-- <b-tooltip
          :target="`tooltip-target-${data.index}`"
          triggers="hover"
          boundary-padding="10"
        >
          <span>
            {{ data.value }}
          </span>
        </b-tooltip> -->
        </template>
        <template #cell(quote_folio)="data">
          <router-link
            v-if="data.item.quote_id"
            :to="{ name: 'purchaseOrder', params: { quoteId: data.item.quote_id } }"
            class="text-primary font-weight-bolder"
          >
            #{{ data.item.quote_folio }}
          </router-link>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(expense_date)="data">
          <div>{{ data.value | date }}</div>
        </template>

        <template #cell(project)="data">
          <div>{{ projectName(data.item) }}</div>
        </template>

        <template #cell(quantity)="data">
          <div>{{ data.value }}</div>
        </template>

        <template #cell(unit_type)="data">
          <div>{{ $tc(unitsFilter(data.value), data.item.quantity) }}</div>
        </template>

        <template #cell(unit_price)="data">
          <div>${{ data.value | money }}</div>
        </template>

        <template #cell(total)="data">
          <div>${{ data.value | money }}</div>
        </template>

        <template #cell(expense_status)="data">
          <b-dropdown
            :id="data.item.id"
            class="p-0"
            size="md"
            dropright
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <b-badge
                :variant="statusColor(data.value)"
                class="px-1"
              >
                {{ $t(data.value) }}
                <feather-icon
                  icon="ChevronDownIcon"
                  size="20"
                />
              </b-badge>
            </template>
            <b-dropdown-item
              v-for="(status, key) in expenseStatusOptions"
              :key="key"
              @click="changeStatus(data.item.id, status.value)"
            >
              {{ $t(status.value) }}
            </b-dropdown-item>
          </b-dropdown>
        </template>

        <template #cell(tools)="data">
          <b-dropdown
            id="dropdown-1"
            class="p-0"
            size="sm"
            dropright
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="18"
                color="#000"
              />
            </template>
            <b-dropdown-item @click="removeExpense(data.item.id)">
              <span class="text-danger">{{ $t('delete') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="openEditModal(data.item)">
              {{ $t('edit') }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </div>

    <b-modal
      id="edit-expense"
      :title="$t('edit')"
      @ok="editExpense()"
      @cancel="expenseToBeEdited = {}"
    >
      <b-row>
        <b-col sm="12">
          <b-form-group
            class="mb-0"
            :label="$t('date')"
            label-for="input-sm"
          >
            <b-form-datepicker
              v-model="expenseToBeEdited.expense_date"
              :date-format-options="{
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
              }"
              size="lg"
              class="font-weight-bolder"
            />
          </b-form-group>
        </b-col>
        <b-col sm="12">
          <b-form-group
            class="mb-0"
            :label="$t('time')"
            label-for="input-sm"
          >
            <b-form-timepicker
              v-model="expenseToBeEdited.expense_time"
              locale="es"
              :hour12="false"
              size="lg"
              hide-header
              no-close-button
            />
          </b-form-group>
        </b-col>
        <b-col sm="12">
          <b-form-group
            class="mb-0"
            :label="$t('unitPrice')"
            label-for="input-sm"
          >
            <b-form-input
              v-model="expenseToBeEdited.unit_price"
              min="1"
              class="qty-field"
              autocomplete="off"
              type="number"
            />
          </b-form-group>
        </b-col>
        <b-col sm="12">
          <b-form-group
            class="mb-0"
            :label="$t('quantity')"
            label-for="input-sm"
          >
            <b-form-input
              v-model="expenseToBeEdited.quantity"
              min="1"
              class="qty-field"
              autocomplete="off"
              type="number"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="view-expense"
      :title="$t('dataSaved')"
      hide-footer
    >
      <b-row>
        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('date')">
            <p class="mb-0 font-weight-bold">
              {{ formatDate(expenseToBeEdited.expense_date) }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('category')">
            <p class="mb-0">
              {{ expenseToBeEdited.expense_type_catalog ? expenseToBeEdited.expense_type_catalog.name : '-' }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('name')">
            <p class="mb-0">
              {{ expenseToBeEdited.name }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('brand')">
            <p class="mb-0">
              {{ expenseToBeEdited.brand }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="12"
          class="mb-1"
        >
          <b-form-group :label="$t('description')">
            <p class="mb-0">
              {{ expenseToBeEdited.description }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Nueva fila para Cotización -->
        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('quote')">
            <router-link
              v-if="expenseToBeEdited.quote_id"
              :to="{ name: 'purchaseOrder', params: { quoteId: expenseToBeEdited.quote_id } }"
              class="text-primary font-weight-bolder"
            >
              #{{ expenseToBeEdited.quote_folio }}
            </router-link>
            <p
              v-else
              class="mb-0 text-muted"
            >
              -
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('supplier')">
            <p class="mb-0">
              {{ expenseToBeEdited.supplier }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('quantity')">
            <p class="mb-0">
              {{ expenseToBeEdited.quantity }}
            </p>
          </b-form-group>
        </b-col>
        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group
            class="mb-0"
            :label="$t('unitPrice')"
            label-for="input-sm"
          >
            <p class="mb-0">
              {{ formatCurrency(expenseToBeEdited.unit_price) }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          md="6"
          class="mb-1"
        >
          <b-form-group :label="$t('amount-2')">
            <p class="mb-0">
              {{ formatCurrency(expenseToBeEdited.total) }}
            </p>
          </b-form-group>
        </b-col>

        <!-- <b-col md="12" class="mb-1">
          <b-form-group class="mb-0" :label="$t('status')" label-for="input-sm">
            <p class="mb-0">{{ expenseToBeEdited.expense_status }}</p>
          </b-form-group>
        </b-col> -->
      </b-row>
    </b-modal>
  </b-card>
</template>

<script>
import { BTable, BSkeletonTable } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TableEmptyState from '@/components/TableEmptyState.vue'
import { expenseDateForApi, localDateOf, localTimeOf } from '@/utils/expenseDates'

export default {
  components: {
    BSkeletonTable,
    TableEmptyState,
    BTable,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    expenses: {
      type: Array,
      default() {
        return []
      },
    },
    projectId: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      default: null,
    },
    groupId: {
      type: [String, Number],
      default: null,
    },
    showProjectColumn: {
      type: Boolean,
      default: false,
    },
    groupedExpenseProjectMap: {
      type: Object,
      default() {
        return {}
      },
    },
    isMaterial: {
      type: Boolean,
      default() {
        return true
      },
    },
  },
  data() {
    return {
      sortBy: 'expense_date',
      sortDesc: true,
      expenseToBeEdited: {
        expense_type_catalog: {},
      },
      expenseStatusOptions: [
        { value: 'paid' },
        { value: 'credit' },
        { value: 'expired' },
        { value: 'topay' },
      ],
      fieldsForMaterial: [
        {
          key: 'expense_date',
          label: this.$t('date'),
          sortable: true,
        },
        {
          key: 'expense_type_catalog.name',
          label: this.$t('category'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('name'),
          sortable: true,
        },
        {
          key: 'total',
          label: this.$t('amount-2'),
          sortable: true,
        },
        {
          key: 'description',
          label: this.$t('description'),
          tdClass: 'text-center',
        },
        {
          key: 'supplier',
          label: this.$t('supplier'),
          sortable: true,
        },
        // {
        //   key: 'expense_create_type',
        //   label: this.$t('type'),
        // },
        {
          key: 'quantity',
          label: this.$t('quantity'),
          sortable: true,
        },
        {
          key: 'unit_type',
          label: this.$t('unit'),
          sortable: true,
        },
        {
          key: 'unit_price',
          label: this.$t('unitPrice'),
          sortable: true,
        },
        // {
        //   key: 'total',
        //   label: this.$t('amount-2'),
        // },
        {
          key: 'expense_status',
          label: this.$t('status'),
          sortable: true,
        },
        {
          key: 'tools',
          label: this.$t('tools'),
        },
      ],
      fields: [
        {
          key: 'expense_date',
          label: this.$t('date'),
          sortable: true,
        },
        {
          key: 'expense_type_catalog.name',
          label: this.$t('category'),
          sortable: true,
        },
        {
          key: 'name',
          label: this.$t('name'),
          sortable: true,
        },
        {
          key: 'quote_folio',
          label: this.$t('quote'),
          sortable: false,
        },
        {
          key: 'total',
          label: this.$t('amount-2'),
          sortable: true,
        },
        // {
        //   key: 'brand',
        //   label: this.$t('brand'),
        // },
        {
          key: 'description',
          label: this.$t('description'),
          tdClass: 'text-center',
        },
        // {
        //   key: 'supplier',
        //   label: this.$t('supplier'),
        // },
        // {
        //   key: 'expense_create_type',
        //   label: this.$t('type'),
        // },
        // {
        //   key: 'quantity',
        //   label: this.$t('quantity'),
        // },
        // {
        //   key: 'unit_price',
        //   label: this.$t('unitPrice'),
        // },
        {
          key: 'expense_status',
          label: this.$t('status'),
          sortable: true,
        },
        {
          key: 'tools',
          label: this.$t('tools'),
        },
      ],
    }
  },
  computed: {
    ...mapGetters('expenses', ['expensesPagination']),
    ...mapGetters('projects', ['project', 'projects']),
    ...mapGetters('app', ['unitsFilter', 'isSmallScreen']),
    scopeProjectId() {
      return this.projectId || this.$route.params.projectId || null
    },
    scopeCustomerId() {
      return this.customerId || this.$route.params.customerId || null
    },
    scopeGroupId() {
      return this.groupId || this.$route.params.groupedExpenseId || null
    },
    projectLookup() {
      const lookup = {}
      if (Array.isArray(this.projects)) {
        this.projects.forEach(project => {
          if (project && project.id) {
            lookup[String(project.id)] = project.name || ''
          }
        })
      }
      return lookup
    },
    tableFields() {
      const base = this.isMaterial ? this.fieldsForMaterial : this.fields
      const fields = [...base]
      if (this.showProjectColumn) {
        const exists = fields.some(field => field.key === 'project')
        if (!exists) {
          const projectField = {
            key: 'project',
            label: this.$t('project'),
            sortable: true,
          }
          const dateIndex = fields.findIndex(field => field.key === 'expense_date')
          if (dateIndex >= 0) {
            fields.splice(dateIndex + 1, 0, projectField)
          } else {
            fields.unshift(projectField)
          }
        }
      }
      return fields
    },
    sortedExpenses() {
      if (!this.sortBy) return this.expenses

      return [...this.expenses].sort((a, b) => {
        const getNestedValue = (obj, path) => path.split('.').reduce((o, key) => o?.[key], obj)

        let valA = getNestedValue(a, this.sortBy)
        let valB = getNestedValue(b, this.sortBy)

        if (this.sortBy === 'project') {
          valA = this.projectName(a)
          valB = this.projectName(b)
        }

        if (valA && typeof valA === 'object' && valA.name) {
          valA = valA.name
        }
        if (valB && typeof valB === 'object' && valB.name) {
          valB = valB.name
        }

        // Orden por número
        if (!Number.isNaN(parseFloat(valA)) && !Number.isNaN(parseFloat(valB))) {
          return this.sortDesc ? valB - valA : valA - valB
        }

        // Orden por fecha
        if (this.sortBy.includes('date') || this.sortBy.includes('at')) {
          return this.sortDesc
            ? new Date(valB) - new Date(valA)
            : new Date(valA) - new Date(valB)
        }

        // Orden por texto
        return this.sortDesc
          ? String(valB).localeCompare(String(valA))
          : String(valA).localeCompare(String(valB))
      })
    },
  },
  methods: {
    // "Todos mis egresos" es de la cuenta, no de una obra, asi que la ruta no trae el
    // projectId. Se toma el del propio egreso: sin el, el enlace armaba una url sin obra y
    // la pantalla destino quedaba sin proyecto y sin menu.
    groupLinkTarget(item) {
      const projectId = item?.project_id || this.scopeProjectId
      if (!projectId || !item?.grouped_expense_id) return null

      return {
        name: 'expensesByGroup',
        params: { projectId, groupedExpenseId: item.grouped_expense_id },
      }
    },

    ...mapActions('expenses', [
      'updateExpenseStatus',
      'fetchExpenses',
      'updateExpense',
      'deleteExpense',
    ]),
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.$emit('sort-changed', ctx)
    },
    changeStatus(expenseId, status) {
      this.updateExpenseStatus({ expenseId, status }).then(() => {
        this.fetchExpenses({
          projectId: this.scopeProjectId,
          customerId: this.scopeCustomerId,
          groupId: this.scopeGroupId,
          pagination: this.expensesPagination,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('statusUpdated'),
            icon: 'SendIcon',
            variant: 'success',
          },
        })
      })
    },
    openEditModal(expense) {
      this.expenseToBeEdited = {
        ...expense,
        expense_date: localDateOf(expense.expense_date),
        expense_time: localTimeOf(expense.expense_date),
        quote_id: expense.quote_id,
        quote_folio: expense.quote_folio,
      }

      this.$bvModal.show('edit-expense')
    },
    editExpense() {
      this.updateExpense({
        ...this.expenseToBeEdited,
        expense_date: expenseDateForApi(this.expenseToBeEdited.expense_date, this.expenseToBeEdited.expense_time),
      }).then(() => {
        this.fetchExpenses({
          projectId: this.scopeProjectId,
          customerId: this.scopeCustomerId,
          groupId: this.scopeGroupId,
          pagination: this.expensesPagination,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('expenseUpdated'),
            icon: 'SendIcon',
            variant: 'success',
          },
        })
      })
      this.$bvModal.hide('edit-expense')
    },
    removeExpense(expenseId) {
      this.deleteExpense(expenseId).then(() => {
        this.fetchExpenses({
          projectId: this.scopeProjectId,
          customerId: this.scopeCustomerId,
          groupId: this.scopeGroupId,
          pagination: this.expensesPagination,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('expenseDeleted'),
            icon: 'SendIcon',
            variant: 'warning',
          },
        })
      })
      this.$bvModal.hide('edit-expense')
    },
    statusColor(status) {
      switch (status) {
        case 'paid':
          return 'success'
        case 'credit':
          return 'warning'
        case 'expired':
          return 'danger'
        case 'topay':
          return 'info'
        default:
          return ''
      }
    },
    viewInfoExpenseModal(expense) {
      this.expenseToBeEdited = { ...expense }
      this.$bvModal.show('view-expense')
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString()
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value)
    },
    cardTitle(item) {
      if (!this.isMaterial) return item.description || item.name
      return item.name
    },
    projectName(item) {
      if (!item) return '-'
      if (item.project_name) return item.project_name
      const projectId = item.project_id

      if (projectId && this.projectLookup[String(projectId)]) {
        return this.projectLookup[String(projectId)]
      }

      return '-'
    },
  },
}
</script>

<style lang="scss">
.table-responsive-mobile {
  width: 100%;
  overflow-x: auto;

  table {
    min-width: 1000px; // Ajusta este valor según cuántas columnas tengas
  }
}
</style>

<style lang="scss" scoped>
$exp-primary: #340668;
$exp-line: #eceaf1;
$exp-muted: #6b7280;

.expense-cards {
  display: flex;
  flex-direction: column;
}

.expense-card {
  border-bottom: 1px solid $exp-line;
  padding: 0.85rem 1rem;

  &:last-child {
    border-bottom: 0;
  }
}

.expense-card__top {
  align-items: baseline;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.expense-card__title {
  color: $exp-primary;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;

  &--plain {
    color: #111827;
  }
}

.expense-card__amount {
  color: #111827;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.expense-card__sub {
  color: $exp-muted;
  font-size: 0.82rem;
  margin-top: 0.15rem;
}

.expense-card__meta {
  align-items: center;
  color: $exp-muted;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 0.35rem;
  margin-top: 0.35rem;
}

.expense-card__quote {
  color: $exp-primary;
  font-weight: 600;
}

.expense-card__actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 0.6rem;
}
</style>
